import { Modal, type ModalProps } from '@app/components'
import { showMessageNotification, showMessagesNotifications, useUploadEnrollments } from '@app/lib'
import { useSugarContext } from '@app/providers'
import {
  ActionIcon,
  Anchor,
  Button,
  Group,
  List,
  LoadingOverlay,
  RingProgress,
  Stack,
  Text,
  useMantineTheme
} from '@mantine/core'
import { Dropzone, type FileWithPath, MS_EXCEL_MIME_TYPE } from '@mantine/dropzone'
import { IconUpload, IconX, IconFile } from '@tabler/icons-react'
import { find } from 'lodash'
import { type FC, useCallback, useMemo, useState } from 'react'

export interface UploadEnrollmentsModalProps extends ModalProps {
  schoolYearId?: string | null
}

export const UploadEnrollmentsModal: FC<UploadEnrollmentsModalProps> = ({ onClose, schoolYearId, ...props }) => {
  const sugar = useSugarContext()
  const schoolYear = useMemo(
    () => (schoolYearId ? find(sugar?.options?.enrollmentReportingSchoolYears, { value: schoolYearId }) : null),
    [schoolYearId, sugar?.options?.enrollmentReportingSchoolYears]
  )
  const { mutateAsync, isPending, progress } = useUploadEnrollments()
  const [file, setFile] = useState<FileWithPath | null>(null)
  const theme = useMantineTheme()

  const handleClose = useCallback(() => {
    setFile(null)
    onClose()
  }, [onClose])

  return (
    <Modal
      onClose={handleClose}
      title={`Import/Export ${schoolYear?.label} Enrollments`}
      size='lg'
      {...props}
    >
      <LoadingOverlay
        visible={isPending}
        loader={
          <RingProgress
            sections={[{ value: progress, color: 'navy' }]}
            label={
              <Text
                align='center'
                color='blue'
                fw='bold'
              >
                {progress}%
              </Text>
            }
          />
        }
      />
      <Stack spacing='xs'>
        <Text fw='bold'>3YO Enrollment Reporting Reminders</Text>
        <Group
          pr='md'
          noWrap
        >
          <List size='sm'>
            <List.Item>
              <Text fw='bold'>
                DOWNLOAD a reporting template EACH TIME for bulk update and/or edit via Import/Export (upper right-hand
                corner) to edit
              </Text>
            </List.Item>
            <List.Item>Use only available columns — do not add new ones</List.Item>
            <List.Item>Double-check student name spelling</List.Item>
            <List.Item>Enter DOB in MM/DD/YEAR format</List.Item>
            <List.Item>Always use drop-downs for columns H, K, M–S</List.Item>
            <List.Item>Student status = Pending until attestation is completed</List.Item>
            <List.Item>Complete attestation between 1st–15th each month for prior month funding</List.Item>
          </List>
        </Group>
        <Anchor
          href={`/api/enrollments/${schoolYearId}`}
          size='sm'
          target='_self'
        >
          Download Current {schoolYear?.label} Enrollments
        </Anchor>
        <Text size='sm'>To import enrollment changes please upload your updated template below:</Text>
        <Dropzone
          accept={MS_EXCEL_MIME_TYPE}
          disabled={!!file}
          maxSize={30_000_000}
          multiple={false}
          onDrop={(dropped) => setFile(dropped[0])}
          onReject={(fileRejections) => {
            if (fileRejections[0].errors[0].code === 'file-too-large') {
              showMessageNotification({
                message: 'Max file size is 30mb!',
                type: 'warning'
              })
            }
          }}
        >
          <Group
            position='center'
            spacing='xl'
            style={{ minHeight: 200, pointerEvents: 'none' }}
          >
            <Dropzone.Accept>
              <IconUpload
                size='3.2rem'
                stroke={1.5}
                color={theme.colors[theme.primaryColor][6]}
              />
              <Text>Drop the updated file here, or click to select a file.</Text>
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX
                size='3.2rem'
                stroke={1.5}
                color={theme.colors.red[6]}
              />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconFile
                size='3.2rem'
                stroke={1.5}
              />
            </Dropzone.Idle>
            <Text>Drop the updated file here, or click to select a file.</Text>
          </Group>
        </Dropzone>
        {file && (
          <Group
            noWrap
            position='apart'
          >
            <Text
              fw='bold'
              size='sm'
            >
              {file.name}
            </Text>
            <ActionIcon onClick={() => setFile(null)}>
              <IconX
                color='red'
                size={18}
              />
            </ActionIcon>
          </Group>
        )}
        <Group
          mt='lg'
          position='right'
        >
          <Button
            disabled={!file}
            leftIcon={<IconUpload size={18} />}
            onClick={async () => {
              if (file && schoolYearId) {
                const { success, messages } = await mutateAsync({
                  file,
                  schoolYearId
                })

                if (success) {
                  handleClose()
                }

                showMessagesNotifications(messages)
              }
            }}
          >
            Import Enrollments
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
